"use client";

import { useEffect, useState } from "react";
import { DynamicPageRenderer } from "@/components/dynamic-page-renderer";
import { PageSkeleton } from "@/components/sections";
import { getCmsPage } from "@/lib/api";

interface DynamicSection {
  id?: string;
  sectionKey: string;
  title?: string | null;
  content: Record<string, unknown>;
  isEnabled: boolean;
}

interface DynamicPage {
  title?: string;
  description?: string | null;
  sections?: DynamicSection[];
}

export function CmsPageClient({ slug }: { slug: string }) {
  const [page, setPage] = useState<DynamicPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadPage = async () => {
      setLoading(true);
      setError(false);

      try {
        const data = await getCmsPage(slug);
        if (cancelled) {
          return;
        }

        if (!data || !Array.isArray(data.sections)) {
          setPage(null);
          setError(true);
          return;
        }

        setPage(data as DynamicPage);
      } catch {
        if (!cancelled) {
          setPage(null);
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadPage();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return <PageSkeleton />;
  }

  if (error || !page) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center sm:px-6 lg:px-8">
        <h1 className="text-3xl font-semibold text-foreground">Page not available</h1>
        <p className="mt-3 text-muted-foreground">The latest CMS content could not be loaded right now.</p>
      </div>
    );
  }

  return <DynamicPageRenderer page={page} />;
}